/* A foreground search modal. Prestige owns focus trapping, scroll locking,
 * dismissal and trigger state; NwpSearch owns the live search requests. */
import 'theme';
import { animate, timeline } from 'vendor';

const initialized = new Map();

function init(panel) {
  if (initialized.has(panel) || !panel.shadowRoot) return;
  if (panel.open) {
    panel.addEventListener('dialog:after-hide', () => init(panel), { once: true });
    return;
  }
  const owner = panel.closest('.shopify-section');
  const cleanup = new AbortController();
  const { signal } = cleanup;
  let entering = false;
  let closing = false;
  let reopen = false;
  const input = () => panel.querySelector('input[name="q"]');
  const isTrigger = node => node?.closest?.('.header__search-link [aria-controls]')?.getAttribute('aria-controls') === panel.id;
  const motion = entering => {
    try {
      panel.querySelector('nwp-search')?.resize?.();
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      return timeline([
        [panel.getShadowPartByName('overlay'), { opacity: entering ? [0, 1] : [1, 0] }, { duration: reduced ? 0 : .18 }],
        [panel.getShadowPartByName('content'), {
          opacity: entering ? [0, 1] : [1, 0],
          transform: entering ? ['translateY(8px) scale(.99)', 'translateY(0) scale(1)'] : ['translateY(0) scale(1)', 'translateY(4px) scale(.995)'],
        }, { at: '<', duration: reduced ? 0 : entering ? .22 : .16, easing: [.2, .65, .3, 1] }],
      ]);
    } catch {
      return animate(panel, {}, { duration: 0 });
    }
  };
  Object.defineProperties(panel, {
    // The sticky header establishes a transformed containing block. Native
    // reparenting lets the overlay cover the entire page, including navigation.
    shouldAppendToBody: { configurable: true, get: () => true },
    createEnterAnimationControls: { configurable: true, value: () => motion(true) },
    createLeaveAnimationControls: { configurable: true, value: () => motion(false) },
  });
  panel.setAttribute('aria-modal', 'true');
  // Activate Prestige's native trap at mount: waiting for the visual entrance
  // left early Tab presses uncontained and could later overwrite user focus.
  panel.setAttribute('trap-focus-on-mount', '');
  panel.setAttribute('initial-focus', 'input[name="q"]');
  panel.tabIndex = -1;
  panel.controls.forEach(trigger => {
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', String(panel.open));
  });
  panel.addEventListener('dialog:before-show', () => {
    entering = true;
    panel.setAttribute('data-nwp-dialog-visible', '');
    document.querySelectorAll('.header details[aria-expanded="true"]').forEach(details => details.parentElement.toggle?.(false));
    document.querySelectorAll('.nwp-header-popout[open]').forEach(other => other.hide());
  }, { signal });
  panel.addEventListener('dialog:after-show', () => { entering = false; }, { signal });
  document.addEventListener('dialog:before-show', event => {
    if (!panel.open || event.target === panel || panel.contains(event.target)) return;
    // A pending Add to Cart can finish while Search is open. Hand off the
    // foreground without restoring focus into the now-unrelated Search trigger.
    panel.focusTrap.deactivate({ returnFocus: false });
    panel.hide();
  }, { capture: true, signal });
  panel.addEventListener('dialog:before-hide', () => { entering = false; closing = true; }, { signal });
  panel.addEventListener('dialog:after-hide', () => {
    closing = false;
    panel.removeAttribute('data-nwp-dialog-visible');
    if (reopen) {
      reopen = false;
      // Start after the native hide completion has restored the owning section.
      queueMicrotask(() => {
        if (!panel.isConnected) return;
        panel.show();
        // A queued touch reopen no longer has the original click handler to
        // focus its field; the native coarse-pointer trap keeps initialFocus=false.
        input()?.focus({ preventScroll: true });
      });
    }
  }, { signal });
  document.addEventListener('keydown', event => {
    // Native focus-trap listeners attach after entry. Escape should also work
    // during that short animation, while native dismissal owns settled state.
    if (!entering || !panel.open || event.key !== 'Escape' || event.isComposing) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    panel.hide();
  }, { capture: true, signal });
  document.addEventListener('click', event => {
    if (!isTrigger(event.target)) return;
    // WebKit does not focus a clicked link by default. Establish the return
    // target before Prestige captures focus and mounts the modal.
    event.target.closest('[aria-controls]').focus({ preventScroll: true });
    if (!closing) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    reopen = true;
  }, { capture: true, signal });
  document.addEventListener('click', event => {
    // The native body listener has already mounted the dialog. Focus during
    // the original tap so mobile browsers can open their software keyboard.
    if (isTrigger(event.target) && panel.open) input()?.focus({ preventScroll: true });
  }, { signal });
  initialized.set(panel, { owner, cleanup });
}

const initAll = () => document.querySelectorAll('header-search.nwp-search-dialog').forEach(init);
await customElements.whenDefined('header-search');
initAll();
document.addEventListener('shopify:section:load', initAll);
document.addEventListener('shopify:section:unload', event => {
  for (const [panel, { owner, cleanup }] of initialized) {
    if (owner !== event.target && !event.target.contains(panel)) continue;
    cleanup.abort();
    if (panel.isConnected) panel.remove();
    initialized.delete(panel);
  }
});
